// The find_issues behavior's job and narration contract — the intelligence
// agents (Fracture, Compass, Keystone, Meridian, Condition Watch). Detection is
// exact maths in the shared pure cores (intelligenceCores.ts and friends); a
// model only words a finding that was already computed. One copy for the
// browser narrator (src/lib/intelligence/llmIntelligenceService.ts) and the
// server one (ambient/lib/narration.ts), which used to carry the same prompt
// text twice.

import { buildInstructionStack, type AgentCard } from './instructionStackCore.ts';
import { LANGUAGE_NAMES } from './estimateValueCore.ts';

/** The narration contract — the appendix of the instruction stack. */
export const FINDINGS_NARRATION_SYSTEM =
  'You write for busy executives running a prioritization process. ' +
  'You are given exact, pre-computed evidence — never contradict or inflate it, ' +
  'never invent numbers. Be specific, concrete, and direct. ' +
  'Respond with valid JSON only: {"summary": string, "detail": string}.';

export const FINDINGS_JOB = {
  outputContract:
    'Per issue the detector found: one finding — type, severity, the items involved, the exact evidence, ' +
    'and a one-sentence summary with a short explanation in plain words. A finding is advice: accepting it ' +
    'changes nothing by itself.',
  skillStack: ['Exact detection over the workspace record', 'Severity ranking', 'Evidence narration'],
  reads: ['the workspace record the detector reads (scores, objectives, relationships, commitments, signals)'],
  proposes: ['findings, each with its evidence, for a person to accept or dismiss'],
  /** The narration bound; an administrator may lower these (§2 H). */
  modelDefaults: { temperature: 0.3, maxTokens: 1024 },
} as const;

export function findingsAgentCard(capabilityId: string, role: string, goal: string): AgentCard {
  return {
    capabilityId,
    role,
    goal,
    permissions: { read: [...FINDINGS_JOB.reads], propose: [...FINDINGS_JOB.proposes] },
    memory: 'session',
    skillStack: [...FINDINGS_JOB.skillStack],
    outputContract: FINDINGS_JOB.outputContract,
  };
}

/**
 * The narration system prompt: the instruction stack with the workspace text as
 * a subordinate overlay — it shapes the wording, never the finding (§2 H).
 */
export function buildFindingsSystemPrompt(params: {
  card: AgentCard;
  overlay?: string | null;
  language?: string | null;
}): string {
  const overlay = params.overlay?.trim();
  const language = params.language && LANGUAGE_NAMES[params.language]
    ? `Write the summary and detail in ${LANGUAGE_NAMES[params.language]}. Keep JSON keys exactly as given.`
    : null;
  return buildInstructionStack({
    card: params.card,
    capabilityAppendix: [FINDINGS_NARRATION_SYSTEM, language].filter(Boolean).join('\n'),
    workspaceOverlay: overlay || null,
  }).systemPrompt;
}
